import { Controller } from "@hotwired/stimulus"

// Shows a Postmark-rendered email as a faithful miniature, and hands the same
// HTML to the page-level dialog when the operator wants to read it.
//
// The sidebar column is ~341px wide, which is narrower than an iPhone SE. An
// iframe's CSS width *is* the viewport its document lays out against, so an
// un-scaled preview there renders the email's mobile breakpoint at a width no
// phone has — the "unnaturally slim" result. Scaling instead lets the iframe
// keep a true 600px viewport and shrinks the rendered pixels, so the thumbnail
// is the desktop email seen small rather than a different email.
export default class extends Controller {
  static targets = ["viewport", "frame"]

  static values = {
    // The width the email is authored against; the scale is relative to this.
    width: { type: Number, default: 600 },
    subject: String,
    locale: String
  }

  connect() {
    this.fit()

    // The grid collapses to a single column below `lg`, which roughly triples
    // the viewport width without a page load, so a connect-time scale would go
    // stale in place.
    this.observer = new ResizeObserver(() => this.fit())
    this.observer.observe(this.viewportTarget)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  fit() {
    const width = this.viewportTarget.clientWidth

    // A hidden locale panel measures 0, and scale(0) would blank the thumbnail
    // until the observer caught up. Leaving the markup's approximate scale in
    // place means the panel is already right when its tab is clicked.
    if (width === 0) return

    // Capped at 1: a viewport wider than the authored width should show the
    // email at its true size, not blow it up past the fidelity of the render.
    const scale = Math.min(width / this.widthValue, 1)

    this.frameTarget.style.transform = `scale(${scale})`
  }

  // The dialog lives outside this controller's Turbo Frame, so this reaches it
  // by event rather than by element lookup. Passing the srcdoc along means the
  // dialog reuses the HTML already fetched here instead of making a second
  // Postmark call.
  expand() {
    this.dispatch("expand", {
      prefix: "email-preview",
      detail: {
        srcdoc: this.frameTarget.srcdoc,
        subject: this.subjectValue,
        locale: this.localeValue
      }
    })
  }
}
